import React, { useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";

import { ReactComponent as DownloadSvg } from "../../assets/icons/Icon/DKT/Download Icon.svg";

import { AssetType } from "@swo/shared";
import { ContentfulImage } from "../_shared-components/image-related/ContentfulImage";
import { CSS_MQ } from "../../styles/breakpoints";
import { CloseButtonSvg, LeftArrowSvg, RightArrowSvg } from "../../assets";
import { SmallUnstyledLoadingSpinner } from "../../routes/modal-loading-spinner";
import { useCurrentWidth } from "../../hooks/use-current-width";
import { useEqualState } from "../../hooks";
import { CSS_COLORS } from "../../styles/styles-css-variables";

export function GalleryModal(props: {
  images: Array<{ image: AssetType; rowIndex: number; indexInRow: number }>;
  close: () => void;
  initiallySelected: { rowIndex: number; indexInRow: number };
}) {
  const { images } = props;

  const [size, setSize] = useEqualState(2000);
  const screenSize = useCurrentWidth();

  useEffect(() => {
    if (screenSize) {
      if (screenSize === "xs" || screenSize === "sm") {
        setSize(600);
      } else if (screenSize === "md") {
        setSize(1000);
      } else {
        setSize(2000);
      }
    }
  }, [screenSize]);

  function findByGridIndex() {
    return (
      images.findIndex(
        img =>
          img.rowIndex === props.initiallySelected.rowIndex && img.indexInRow === props.initiallySelected.indexInRow,
      ) || 0
    );
  }

  const [imageIndex, setImageIndex] = useState(findByGridIndex());

  function onClickLeft() {
    const galleryLength = images.length;

    const previousIndex = (imageIndex + galleryLength - 1) % galleryLength;
    setImageIndex(previousIndex);
  }

  function onClickRight() {
    const galleryLength = images.length;

    const nextIndex = (imageIndex + 1) % galleryLength;
    setImageIndex(nextIndex);
  }

  function keysNavigation(e: KeyboardEvent) {
    if (e.keyCode === 37) {
      onClickLeft();
    }

    if (e.keyCode === 39) {
      onClickRight();
    }

    if (e.keyCode === 27) {
      props.close();
    }
  }

  const ref = useRef<HTMLImageElement | null>(null);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.addEventListener("keydown", keysNavigation);

    return () => {
      document.removeEventListener("keydown", keysNavigation);
    };
  });

  const elementStyles = css`
    width: 55px;
    height: 55px;

    padding: 5px;

    cursor: pointer;

    color: ${CSS_COLORS.White};
    background-color: rgba(0, 0, 0, 0.7);
  `;

  const imageAtIdx = props.images[imageIndex]!;

  return (
    <React.Fragment>
      <div
        css={css`
          position: fixed;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 999;
        `}
      >
        <CloseButtonSvg
          onClick={() => props.close()}
          css={css`
            position: absolute;

            right: 0;
            top: 0;

            ${elementStyles};
          `}
        />
        <div
          css={css`
            position: relative;
          `}
          ref={wrapperRef}
        >
          {images.length > 1 && (
            <LeftArrowSvg
              onClick={() => onClickLeft()}
              css={css`
                color: ${CSS_COLORS.White};
                position: absolute;
                top: 50%;

                left: -60px;
                ${CSS_MQ.atLeastMd} {
                  left: -100px;
                }
                transform: translateY(-50%);

                ${elementStyles};
              `}
            />
          )}

          <div
            css={css`
              position: absolute;
              left: calc(50% - 50px);
              top: calc(50% - 50px);
              width: 100px;
              height: 100px;

              display: flex;
              justify-content: center;
              align-items: center;

              z-index: 0;
            `}
          >
            <SmallUnstyledLoadingSpinner
              css={css`
                position: relative;

                width: 40px;
                height: 40px;
              `}
            />
          </div>

          <a
            target={"_blank"}
            download={imageAtIdx.image.fileName}
            href={imageAtIdx.image.url}
            css={css`
              position: absolute;
              z-index: 3;

              //left: 50%;
              //transform: translateX(-50%);
              right: 10px;

              bottom: 15px;
              color: ${CSS_COLORS.White};
              background-color: rgba(0, 0, 0, 70%);

              &:hover {
                background-color: rgba(0, 0, 0, 100%);
              }

              padding: 8px;
              border-radius: 8px;

              text-align: center;

              display: flex;
              justify-content: center;
              align-items: center;
            `}
          >
            <DownloadSvg
              css={css`
                width: 28px;
                height: 28px;
                //margin-right: 10px;
              `}
            />
          </a>

          <ContentfulImage
            key={imageAtIdx.image.url}
            assetId={imageAtIdx.image.sys.id}
            contentfulLink={{
              url: imageAtIdx.image.url,
              noTransparentBackground: true,
              requestedWidth: size,
            }}
            higherDPI={[2]}
            css={css`
              max-height: 90vh;

              width: calc(100vw - 120px);
              ${CSS_MQ.atLeastMd} {
                width: 75vw;
              }

              object-fit: contain;
            `}
            ref={ref}
            attachPictureStyles={css`
              position: relative;

              display: block;
              z-index: 2;
            `}
          />

          {images.length > 1 && (
            <RightArrowSvg
              onClick={() => onClickRight()}
              css={css`
                color: ${CSS_COLORS.White};
                position: absolute;
                top: 50%;

                right: -60px;
                ${CSS_MQ.atLeastMd} {
                  right: -100px;
                }

                transform: translateY(-50%);

                ${elementStyles};
              `}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
}
